import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  MoreVertical,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  Eye,
  Mail,
  Phone,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ConfirmDialog from "../../shared/ConfirmDialog";
import useUser from "../../../hooks/useUser";

export default function UserListView({ renderActions, onPaginationChange }) {
  const USER_TABS = {
    EMPLOYEES: "employees",
    SELLER_USERS: "seller-users",
  };

  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState(USER_TABS.EMPLOYEES);
  const [filterText, setFilterText] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    data: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const navigate = useNavigate();
  const actionMenuRef = useRef(null);

  const { users, deleteUser, fetchUsers, loading } = useUser();

  const handleRefresh = async () => {
    await fetchUsers();
    toast.success("Users refreshed");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionMenu && actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setOpenActionMenu(null);
        setSelectedUser(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionMenu]);

  useEffect(() => {
    fetchUsers();
    renderActions?.({ onRefresh: handleRefresh });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setFilterRole("all");
  }, [activeTab]);

  const isSellerUserType = (type) => {
    return ["SELLER_USER", "POS_USER", "SELLER"].includes((type || "").toUpperCase());
  };

  const getDisplayUserType = (user) => {
    const rawType = (user.type || user.raw?.userType || user.raw?.type || "").toUpperCase();
    if (rawType === "POS_USER" || rawType === "SELLER") return "SELLER_USER";
    if (rawType === "HIGHER_MANAGER") return "TEAM_LEAD";
    return rawType || "—";
  };

  const usersByTab = useMemo(() => {
    return users.filter((u) => {
      const sellerType = isSellerUserType(u.type || u.raw?.userType || u.raw?.type);
      if (activeTab === USER_TABS.SELLER_USERS) return sellerType;
      return !sellerType;
    });
  }, [users, activeTab]);

  const sellerUsersCount = useMemo(() => {
    return users.filter((u) => isSellerUserType(u.type || u.raw?.userType || u.raw?.type)).length;
  }, [users]);

  const employeesCount = users.length - sellerUsersCount;

  const roleList = useMemo(() => {
    const set = new Set();
    usersByTab?.forEach((u) => {
      const uType = getDisplayUserType(u);
      if (uType && uType !== "—") set.add(uType);
    });
    return ["all", ...Array.from(set)];
  }, [usersByTab]);

  const filteredUsers = useMemo(() => {
    return usersByTab.filter((u) => {
      const text = filterText.toLowerCase();
      const sellerNames = (u.raw?.sellers || []).map((s) => s.businessName).filter(Boolean);
      const matchesText = [u.name, u.email, u.phone, u.role, ...sellerNames]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(text));
      const displayType = getDisplayUserType(u);
      const matchesRole = filterRole === "all" || displayType === filterRole;
      return matchesText && matchesRole;
    });
  }, [usersByTab, filterText, filterRole]);

  const sortedUsers = useMemo(() => {
    let list = [...filteredUsers];
    if (!sortConfig.key) return list;
    list.sort((a, b) => {
      const A = (a[sortConfig.key] || "").toLowerCase();
      const B = (b[sortConfig.key] || "").toLowerCase();
      if (A < B) return sortConfig.direction === "asc" ? -1 : 1;
      if (A > B) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredUsers, sortConfig]);

  const totalItems = sortedUsers.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalItems]);

  useEffect(() => {
    onPaginationChange?.({ totalPages, currentPage, totalItems, pageSize, setCurrentPage });
  }, [totalPages, currentPage, totalItems, pageSize]);

  const paginatedUsers = useMemo(() => {
    if (loading) return [];
    const start = (currentPage - 1) * pageSize;
    return sortedUsers.slice(start, start + pageSize);
  }, [sortedUsers, currentPage, pageSize, loading]);

  const deleteNow = async () => {
    try {
      await deleteUser(confirmDialog.data);
      toast.success("User deleted");
    } catch {
      toast.error("Delete failed");
    }
    setConfirmDialog({ isOpen: false, data: null });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={12} className="text-gray-400" />;
    return sortConfig.direction === "asc"
      ? <ArrowUp size={12} className="text-[#C45500]" />
      : <ArrowDown size={12} className="text-[#C45500]" />;
  };

  const roleOrTypeColumnKey = activeTab === USER_TABS.SELLER_USERS ? "type" : "role";
  const roleOrTypeColumnLabel = "User Type";
  const isSellerTab = activeTab === USER_TABS.SELLER_USERS;
  const colSpanCount = 5;

  return (
    <div className="space-y-4">
      {/* Tabs — Amazon style */}
      <div className="flex border-b border-[#D5D9D9]">
        <button
          type="button"
          onClick={() => setActiveTab(USER_TABS.EMPLOYEES)}
          className={`relative px-5 py-2.5 text-[13px] font-semibold transition-colors ${
            activeTab === USER_TABS.EMPLOYEES
              ? "text-[#C45500]"
              : "text-[#565959] hover:text-[#0F1111]"
          }`}
        >
          Employees
          <span className={`ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
            activeTab === USER_TABS.EMPLOYEES ? "bg-[#FFF4E6] text-[#C45500]" : "bg-[#F3F3F3] text-[#565959]"
          }`}>{employeesCount}</span>
          {activeTab === USER_TABS.EMPLOYEES && (
            <span className="absolute bottom-0 left-1 right-1 h-[3px] bg-[#f08804] rounded-t-full" />
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab(USER_TABS.SELLER_USERS)}
          className={`relative px-5 py-2.5 text-[13px] font-semibold transition-colors ${
            activeTab === USER_TABS.SELLER_USERS
              ? "text-[#C45500]"
              : "text-[#565959] hover:text-[#0F1111]"
          }`}
        >
          Seller Users
          <span className={`ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
            activeTab === USER_TABS.SELLER_USERS ? "bg-[#FFF4E6] text-[#C45500]" : "bg-[#F3F3F3] text-[#565959]"
          }`}>{sellerUsersCount}</span>
          {activeTab === USER_TABS.SELLER_USERS && (
            <span className="absolute bottom-0 left-1 right-1 h-[3px] bg-[#f08804] rounded-t-full" />
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
          <Search size={16} className="text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
          />
        </div>
        {activeTab === USER_TABS.EMPLOYEES && (
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 outline-none focus:ring-1 focus:ring-[#f08804]"
          >
            <option value="all">All User Types</option>
            {roleList.filter((r) => r !== "all").map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="bg-slate-50 border-b border-gray-200">
              <th
                onClick={() => handleSort("name")}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
              >
                <div className="flex items-center gap-1.5">
                  User <SortIcon columnKey="name" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Contact
              </th>
              <th
                onClick={() => handleSort(roleOrTypeColumnKey)}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
              >
                <div className="flex items-center gap-1.5">
                  {roleOrTypeColumnLabel} <SortIcon columnKey={roleOrTypeColumnKey} />
                </div>
              </th>
              {isSellerTab && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Seller
                </th>
              )}
              {!isSellerTab && (
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  AM Sellers
                </th>
              )}
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide w-20">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={colSpanCount} className="py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C45500] mx-auto" />
                </td>
              </tr>
            ) : paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={colSpanCount} className="py-12 text-center text-gray-500 text-sm">
                  No users found
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#f08804] to-[#e88b00] flex items-center justify-center text-white text-xs font-semibold">
                        {user.name?.substring(0, 2)?.toUpperCase()}
                      </div>
                      <div>
                        <button
                          onClick={() => navigate(`/users/view/${user.id}`)}
                          className="font-medium text-gray-900 hover:text-[#C45500] hover:underline text-left"
                        >
                          {user.name}
                        </button>
                        {user.raw?.username && (
                          <p className="text-xs text-slate-400">@{user.raw.username}</p>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Mail size={12} className="text-gray-400" />
                        <span className="truncate max-w-[200px]">{user.email || "—"}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Phone size={12} className="text-gray-400" />
                        {user.phone || "—"}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded">
                      {getDisplayUserType(user)}
                    </span>
                  </td>

                  {isSellerTab && (
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(user.raw?.sellers || []).length > 0
                          ? user.raw.sellers.map((s) => (
                              <span key={s.id} className="inline-flex px-2 py-1 text-xs font-medium bg-[#FFF4E6] text-[#C45500] rounded">
                                {s.businessName}
                              </span>
                            ))
                          : <span className="text-xs text-gray-400">—</span>
                        }
                      </div>
                    </td>
                  )}

                  {!isSellerTab && (
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const amCount = Number(user.raw?.accountManagerSellerCount) || 0;
                        if (amCount === 0) {
                          return <span className="text-xs text-gray-300">0</span>;
                        }
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/sellers?accountManager=${encodeURIComponent(user.name)}`);
                            }}
                            title={`View ${amCount} seller${amCount === 1 ? '' : 's'} managed by ${user.name}`}
                            className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-xs font-bold bg-[#FFF4E6] text-[#C45500] rounded border border-[#FFD89E] hover:bg-[#FFE6C7] transition-colors"
                          >
                            {amCount}
                          </button>
                        );
                      })()}
                    </td>
                  )}

                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPos({
                          top: rect.bottom + window.scrollY + 4,
                          left: rect.right - 160 + window.scrollX,
                        });
                        setSelectedUser(user);
                        setOpenActionMenu(openActionMenu === user.id ? null : user.id);
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-md text-gray-500 hover:text-gray-700"
                    >
                      <MoreVertical size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

      </div>

      {/* Action Dropdown */}
      {openActionMenu && selectedUser && (
        <div
          ref={actionMenuRef}
          className="fixed bg-white shadow-lg border border-gray-200 rounded-lg py-1 w-40 z-[9999]"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          <button
            onClick={() => {
              navigate(`/users/view/${selectedUser.id}`);
              setOpenActionMenu(null);
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 w-full"
          >
            <Eye size={14} />
            View
          </button>
          <button
            onClick={() => {
              navigate(`/users/edit/${selectedUser.id}`);
              setOpenActionMenu(null);
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 w-full"
          >
            <Edit2 size={14} />
            Edit
          </button>
          <button
            onClick={() => {
              setConfirmDialog({ isOpen: true, data: selectedUser.id });
              setOpenActionMenu(null);
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-sm text-red-600 w-full"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        type="danger"
        onClose={() => setConfirmDialog({ isOpen: false, data: null })}
        onConfirm={deleteNow}
      />
    </div>
  );
}
